import { useEffect, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import LoadingPopup from '@/shared/components/LoadingPopup/LoadingPopup';
import { AttorneyPage as Attorney } from './features/attorney';
import { AdminPage as Admin } from './features/admin';
import { hasAnyAdminAccess } from '@/shared/config/adminPermissions';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { Toaster } from '@/shared/components/ui/sonner';
import { logger } from '@/shared/utils/logger';
import { ExtSignIn } from '@/pages/extension/SignIn';

// V2
import { V2App } from './v2/V2App';
import { useIsMobile } from './v2/hooks/useIsMobile';
import { HardcodedParamsProvider } from '@/shared/contexts/HardcodedParamsContext';

// Toggle: set to true to always show v2, false for mobile-only
const SHOW_V2 = false;

// SP-initiated SSO: redirect users from app.hourglass.law to their tenant subdomain
const ORG_TENANT_URLS: Record<string, string> = {
  "org_01KH4HH2TTWWGJMS2ZAVV73RC4": "https://namanhowell.hourglass.law",
  "org_01KBR4JTS2NNBHT2KRGHZ9VVVR": "https://taylordaylaw.hourglass.law",
  "org_01K7RGPDSVQHZVZY2MKBDKRQ8M": "https://hourglass.pregodonnell.com",
  "org_01K7JD4563CA4ED2KE4721Y1ZM": "https://hourglass.bpmlaw.com",
  "org_01K7PTWAR69ZHVQTAX9T1BFT7N": "https://hwslawgroup.hourglass.law",
  "org_01KA1NGHMEE35HNYS4AV8ASYE1": "https://hourglass.tysonmendes.com",
  "org_01KRC98DKCDEZRVFGTYPDTAHE8": "https://gouldratner.hourglass.law",
};

export default function App() {
  const { isLoading, user, organizationId, signIn } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const useV2 = SHOW_V2 || isMobile;

  // Auto sign-in — save current path so we can restore it after auth
  useEffect(() => {
    if (!user && !isLoading) {
      const returnTo = window.location.pathname + window.location.search;
      if (returnTo !== '/') {
        sessionStorage.setItem('workos_return_to', returnTo);
      }
      signIn();
    }
  }, [user, isLoading, signIn]);

  // Restore path after sign-in completes
  useEffect(() => {
    if (!user || isLoading) return;
    const returnTo = sessionStorage.getItem('workos_return_to');
    if (returnTo) {
      sessionStorage.removeItem('workos_return_to');
      navigate(returnTo, { replace: true });
    }
  }, [user, isLoading, navigate]);

  // SP-initiated SSO redirect (skip for extension auth flow)
  useEffect(() => {
    if (!user || !organizationId) return;
    if (window.location.pathname.startsWith('/e/')) return;
    if (window.location.hostname !== 'app.hourglass.law') return;
    const tenantUrl = ORG_TENANT_URLS[organizationId];
    if (tenantUrl) {
      setIsRedirecting(true);
      window.location.href = tenantUrl;
    }
  }, [user, organizationId]);

  // Sentry user context
  useEffect(() => {
    if (user) {
      logger.setUser({
        id: user.id,
        email: user.email,
        username: user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.email,
      });
    }
  }, [user]);

  if (isLoading || !user || isRedirecting) {
    return <LoadingPopup show={true} />;
  }

  // Extension auth pages — authenticated but outside the main app shell
  if (window.location.pathname.startsWith('/e/')) {
    return (
      <>
        <Toaster />
        <Routes>
          <Route path="/e/auth/signin" element={<ExtSignIn />} />
        </Routes>
      </>
    );
  }

  if (useV2) {
    return (
      <HardcodedParamsProvider>
        <V2App />
      </HardcodedParamsProvider>
    );
  }

  return (
    <HardcodedParamsProvider>
      <V1App />
    </HardcodedParamsProvider>
  );
}

function V1App() {
  const { user } = useAuth();
  const isAdmin = user ? hasAnyAdminAccess(user.email) : false;

  return (
    <>
      <Toaster />
      <Routes>
        <Route path="/" element={<Navigate to={isAdmin ? '/admin' : '/tracked'} replace />} />
        <Route path="/admin/*" element={
          isAdmin ? <ErrorBoundary><Admin /></ErrorBoundary> : <Navigate to="/tracked" replace />
        } />
        <Route path="/*" element={<ErrorBoundary><Attorney /></ErrorBoundary>} />
      </Routes>
    </>
  );
}
